import { ref, get, update, push, set, remove } from 'firebase/database';
import { createUserWithEmailAndPassword, updatePassword, signInWithEmailAndPassword } from 'firebase/auth';
import { database, auth } from '../firebase/config';
import { User } from '../types/User';
import { Assinatura } from '../types/Assinatura';
import { Pagamento } from '../types/Pagamento';
import { Mensalidade } from '../types/Mensalidade';
import { FireAgentConfig, ParametrosSistema, ClienteComMensalidadeVencida, MetaApiConfig, EmbeddedSignupConfig, EmbeddedSignupResponse } from '../types/FireAgent';
import { PerfilAcesso, Permissoes } from '../types/Permission';
import { SistemaNotificacao } from '../types/Notification';

export interface LinkPagamento {
  id: string;
  nome: string;
  url: string;
  valor: number;
  ativo: boolean;
  dataCriacao: string;
}

export interface Turma {
  id: string;
  nome: string;
  nivelId: string;
  professoresIds: string[];
  diasSemana: string[];
  horarioInicio: string;
  horarioFim: string;
  limiteAlunos: number;
  piscina: string;
  alunosIds: string[];
  observacoes?: string;
  createDate: string;
  updateDate: string;
  ativo: boolean;
  corTurma?: string;
  tipoTurma: string;
}


export interface Empresa {
  id: string;
  createDate: string;
  updateDate: string;
  deleteDate: string;
  email: string;
  telefone: string;
  site: string;
  status: boolean;
  nomeFantasia: string;
  razaoSocial: string;
  cnpj: string;
  inscricaoEstadual: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  logradouro: string;
  cep: string;
  googlePlaceId: string;
  logoUrl?: string;
  redesSociais?: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    twitter?: string;
  };
  coresTema?: {
    primary?: string;
    secondary?: string;
  };
}

class FirebaseService {
  // Usuários
  async getUsuarios(empresaCnpj: string): Promise<User[]> {
    try {
      const usuariosRef = ref(database, `empresas/${empresaCnpj}/usuarios`);
      const snapshot = await get(usuariosRef);
      
      if (snapshot.exists()) {
        const usuarios: User[] = [];
        snapshot.forEach((childSnapshot) => {
          const userData = childSnapshot.val();
          usuarios.push({
            id: childSnapshot.key!,
            ...userData
          });
        });
        return usuarios;
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      throw error;
    }
  }

  async getAlunos(empresaCnpj: string): Promise<User[]> {
    try {
      const alunosRef = ref(database, `empresas/${empresaCnpj}/alunos`);
      const alunosSnapshot = await get(alunosRef);
      
      if (alunosSnapshot.exists()) {
        const alunos: User[] = [];
        alunosSnapshot.forEach((childSnapshot) => {
          const alunoData = childSnapshot.val();
          // Filtrar alunos deletados (soft delete)
          if (!alunoData.deleteDate || alunoData.deleteDate === '') {
            alunos.push({
              id: childSnapshot.key!,
              ...alunoData
            });
          }
        });
        return alunos;
      }
      
      return [];
    } catch (error) {
      console.error('Erro ao buscar alunos:', error);
      throw error;
    }
  }

  async getAllAlunos(empresaCnpj: string): Promise<User[]> {
    try {
      const alunosRef = ref(database, `empresas/${empresaCnpj}/alunos`);
      const alunosSnapshot = await get(alunosRef);
      
      if (alunosSnapshot.exists()) {
        const alunos: User[] = [];
        alunosSnapshot.forEach((childSnapshot) => {
          const alunoData = childSnapshot.val();
          // Incluir TODOS os alunos, mesmo os deletados (para uso em cobranças)
          alunos.push({
            id: childSnapshot.key!,
            ...alunoData
          });
        });
        return alunos;
      }
      
      return [];
    } catch (error) {
      console.error('Erro ao buscar todos os alunos:', error);
      throw error;
    }
  }

  async getProfessores(empresaCnpj: string): Promise<User[]> {
    try {
      const usuarios = await this.getUsuarios(empresaCnpj);
      return usuarios.filter(user => user.cargo === 'professor');
    } catch (error) {
      console.error('Erro ao buscar professores:', error);
      throw error;
    }
  }

  // Criar professor
  async createProfessor(empresaCnpj: string, professorData: Partial<User>): Promise<string> {
    try {
      const usuariosRef = ref(database, `empresas/${empresaCnpj}/usuarios`);
      const newProfessorRef = push(usuariosRef);
      const professorId = newProfessorRef.key!;
      
      const professor: User = {
        id: professorId,
        createDate: new Date().toISOString(),
        updateDate: new Date().toISOString(),
        deleteDate: '',
        enderecoId: professorData.enderecoId || '',
        dataNascimento: professorData.dataNascimento || '',
        nome: professorData.nome || '',
        email: professorData.email || '',
        telefone: professorData.telefone || '',
        cellular: professorData.cellular || professorData.telefone || '',
        fotoUrl: professorData.fotoUrl || '',
        perfilId: '',
        lastLogin: '',
        empresaId: empresaCnpj,
        nivelId: '',
        nivel: '',
        progressoNivel: 0,
        isOnline: false,
        cargo: 'professor',
        cpf: professorData.cpf || '',
        turmas: professorData.turmas || [],
        professorId: '',
        diaVencimento: 0,
        valorMensalidade: 0,
        bAtivo: professorData.bAtivo !== undefined ? professorData.bAtivo : true,
        customerId: professorData.customerId || ''
      };

      await set(newProfessorRef, professor);
      console.log('✅ Professor criado com sucesso:', professorId);
      return professorId;
    } catch (error) {
      console.error('Erro ao criar professor:', error);
      throw error;
    }
  }

  // Atualizar dados completos do professor
  async updateProfessor(empresaCnpj: string, professorId: string, professorData: Partial<User>): Promise<void> {
    try {
      const updates: any = {};
      const updateDate = new Date().toISOString();

      // Atualizar campos
      if (professorData.nome !== undefined) {
        updates[`empresas/${empresaCnpj}/usuarios/${professorId}/nome`] = professorData.nome;
      }
      if (professorData.email !== undefined) {
        updates[`empresas/${empresaCnpj}/usuarios/${professorId}/email`] = professorData.email;
      }
      if (professorData.telefone !== undefined) {
        updates[`empresas/${empresaCnpj}/usuarios/${professorId}/telefone`] = professorData.telefone;
      }
      if (professorData.cellular !== undefined) {
        updates[`empresas/${empresaCnpj}/usuarios/${professorId}/cellular`] = professorData.cellular;
      }
      if (professorData.cpf !== undefined) {
        updates[`empresas/${empresaCnpj}/usuarios/${professorId}/cpf`] = professorData.cpf;
      }
      if (professorData.dataNascimento !== undefined) {
        updates[`empresas/${empresaCnpj}/usuarios/${professorId}/dataNascimento`] = professorData.dataNascimento;
      }
      if (professorData.fotoUrl !== undefined) {
        updates[`empresas/${empresaCnpj}/usuarios/${professorId}/fotoUrl`] = professorData.fotoUrl;
      }
      if (professorData.bAtivo !== undefined) {
        updates[`empresas/${empresaCnpj}/usuarios/${professorId}/bAtivo`] = professorData.bAtivo;
      }
      if (professorData.enderecoId !== undefined) {
        updates[`empresas/${empresaCnpj}/usuarios/${professorId}/enderecoId`] = professorData.enderecoId;
      }

      // Sempre atualizar updateDate
      updates[`empresas/${empresaCnpj}/usuarios/${professorId}/updateDate`] = updateDate;

      await update(ref(database), updates);
      console.log(`✅ Professor ${professorId} atualizado com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar professor:', error);
      throw error;
    }
  }

  async getAdministradores(empresaCnpj: string): Promise<User[]> {
    try {
      const usuarios = await this.getUsuarios(empresaCnpj);
      return usuarios.filter(user => 
        (user.cargo === 'administrador' || user.cargo === 'admin') && user.bAtivo
      );
    } catch (error) {
      console.error('Erro ao buscar administradores:', error);
      throw error;
    }
  }

  // Turmas
  // Criar nova turma
  async createTurma(empresaCnpj: string, turmaData: Partial<Turma>): Promise<string> {
    try {
      const turmasRef = ref(database, `empresas/${empresaCnpj}/turmas`);
      const newTurmaRef = push(turmasRef);
      const turmaId = newTurmaRef.key!;
      
      const now = new Date().toISOString();
      const turma: Turma = {
        id: turmaId,
        nome: turmaData.nome || '',
        tipoTurma: turmaData.tipoTurma || 'regular',
        nivelId: turmaData.nivelId || '',
        horarioInicio: turmaData.horarioInicio || '',
        horarioFim: turmaData.horarioFim || '',
        limiteAlunos: turmaData.limiteAlunos || 0,
        piscina: turmaData.piscina || '',
        observacoes: turmaData.observacoes || '',
        ativo: turmaData.ativo !== undefined ? turmaData.ativo : true,
        professoresIds: Array.isArray(turmaData.professoresIds) ? turmaData.professoresIds : [],
        diasSemana: Array.isArray(turmaData.diasSemana) ? turmaData.diasSemana : [],
        alunosIds: [],
        createDate: now,
        updateDate: now,
        corTurma: turmaData.corTurma || ''
      };

      // Salvar professoresIds e diasSemana como string JSON para manter compatibilidade
      const turmaToSave: any = {
        ...turma,
        professoresIds: JSON.stringify(turma.professoresIds),
        diasSemana: JSON.stringify(turma.diasSemana),
        alunosIds: JSON.stringify(turma.alunosIds),
        ativo: turma.ativo ? 1 : 0
      };

      await set(newTurmaRef, turmaToSave);
      console.log('✅ Turma criada com sucesso:', turmaId);
      
      // Criar notificação para administradores sobre nova turma
      try {
        await this.criarNotificacaoParaTodosAdmins(empresaCnpj, {
          titulo: 'Nova Turma Criada',
          mensagem: `A turma "${turma.nome}" foi criada com sucesso.`,
          tipo: 'success',
          lida: false,
          link: `/turmas`
        });
      } catch (error) {
        console.warn('⚠️ Erro ao criar notificação de nova turma:', error);
      }
      
      return turmaId;
    } catch (error) {
      console.error('❌ Erro ao criar turma:', error);
      throw error;
    }
  }

  // Atualizar dados completos da turma
  async updateTurma(empresaCnpj: string, turmaId: string, turmaData: Partial<Turma>): Promise<void> {
    try {
      const updates: any = {};
      const updateDate = new Date().toISOString();

      // Atualizar campos
      if (turmaData.nome !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/nome`] = turmaData.nome;
      }
      if (turmaData.tipoTurma !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/tipoTurma`] = turmaData.tipoTurma;
      }
      if (turmaData.nivelId !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/nivelId`] = turmaData.nivelId;
      }
      if (turmaData.horarioInicio !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/horarioInicio`] = turmaData.horarioInicio;
      }
      if (turmaData.horarioFim !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/horarioFim`] = turmaData.horarioFim;
      }
      if (turmaData.limiteAlunos !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/limiteAlunos`] = turmaData.limiteAlunos;
      }
      if (turmaData.piscina !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/piscina`] = turmaData.piscina;
      }
      if (turmaData.observacoes !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/observacoes`] = turmaData.observacoes;
      }
      if (turmaData.ativo !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/ativo`] = turmaData.ativo;
      }
      if (turmaData.professoresIds !== undefined) {
        // Salvar como string JSON para manter compatibilidade
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/professoresIds`] = JSON.stringify(turmaData.professoresIds);
      }
      if (turmaData.diasSemana !== undefined) {
        // Salvar como string JSON para manter compatibilidade
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/diasSemana`] = JSON.stringify(turmaData.diasSemana);
      }
      if (turmaData.corTurma !== undefined) {
        updates[`empresas/${empresaCnpj}/turmas/${turmaId}/corTurma`] = turmaData.corTurma;
      }

      // Sempre atualizar updateDate
      updates[`empresas/${empresaCnpj}/turmas/${turmaId}/updateDate`] = updateDate;

      await update(ref(database), updates);
      console.log(`✅ Turma ${turmaId} atualizada com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar turma:', error);
      throw error;
    }
  }

  async getTurmas(empresaCnpj: string): Promise<Turma[]> {
    try {
      console.log('🔍 FirebaseService - Buscando turmas para CNPJ:', empresaCnpj);
      const turmasRef = ref(database, `empresas/${empresaCnpj}/turmas`);
      const snapshot = await get(turmasRef);
      
      if (snapshot.exists()) {
        const turmas: Turma[] = [];
        snapshot.forEach((childSnapshot) => {
          const turmaData = childSnapshot.val();
          console.log('🔍 Debug - Turma individual:', childSnapshot.key, turmaData);
          
          // Processar professoresIds - pode ser string JSON ou array
          let professoresIds: string[] = [];
          if (turmaData.professoresIds) {
            if (typeof turmaData.professoresIds === 'string') {
              try {
                professoresIds = JSON.parse(turmaData.professoresIds);
              } catch (e) {
                console.warn('⚠️ Erro ao fazer parse de professoresIds:', e);
                professoresIds = [];
              }
            } else if (Array.isArray(turmaData.professoresIds)) {
              professoresIds = turmaData.professoresIds;
            }
          }
          
          // Processar alunosIds - pode ser string JSON ou array
          let alunosIds: string[] = [];
          if (turmaData.alunosIds) {
            if (typeof turmaData.alunosIds === 'string') {
              try {
                alunosIds = JSON.parse(turmaData.alunosIds);
              } catch (e) {
                console.warn('⚠️ Erro ao fazer parse de alunosIds:', e);
                alunosIds = [];
              }
            } else if (Array.isArray(turmaData.alunosIds)) {
              alunosIds = turmaData.alunosIds;
            }
          }
          
          // Processar diasSemana - pode ser string JSON ou array
          let diasSemana: string[] = [];
          if (turmaData.diasSemana) {
            if (typeof turmaData.diasSemana === 'string') {
              try {
                diasSemana = JSON.parse(turmaData.diasSemana);
              } catch (e) {
                console.warn('⚠️ Erro ao fazer parse de diasSemana:', e);
                diasSemana = [];
              }
            } else if (Array.isArray(turmaData.diasSemana)) {
              diasSemana = turmaData.diasSemana;
            }
          }
          
          turmas.push({
            id: childSnapshot.key!,
            ...turmaData,
            professoresIds,
            alunosIds,
            diasSemana
          });
        });
        console.log('✅ Turmas carregadas:', turmas.length);
        console.log('🔍 Debug - Turmas detalhadas:', turmas);
        return turmas;
      }
      console.log('❌ Nenhuma turma encontrada');
      return [];
    } catch (error) {
      console.error('❌ Erro ao buscar turmas:', error);
      throw error;
    }
  }


  // Empresa
  async getEmpresa(empresaCnpj: string): Promise<Empresa | null> {
    try {
      const empresaRef = ref(database, `empresas/${empresaCnpj}`);
      const snapshot = await get(empresaRef);
      
      if (snapshot.exists()) {
        return {
          id: empresaCnpj,
          ...snapshot.val()
        };
      }
      return null;
    } catch (error) {
      console.error('Erro ao buscar empresa:', error);
      throw error;
    }
  }

  async updateEmpresa(empresaCnpj: string, empresaData: Partial<Empresa>): Promise<void> {
    try {
      const updates: any = {};
      const updateDate = new Date().toISOString();

      if (empresaData.nomeFantasia !== undefined) { updates[`empresas/${empresaCnpj}/nomeFantasia`] = empresaData.nomeFantasia; }
      if (empresaData.razaoSocial !== undefined) { updates[`empresas/${empresaCnpj}/razaoSocial`] = empresaData.razaoSocial; }
      if (empresaData.cnpj !== undefined) { updates[`empresas/${empresaCnpj}/cnpj`] = empresaData.cnpj; }
      if (empresaData.inscricaoEstadual !== undefined) { updates[`empresas/${empresaCnpj}/inscricaoEstadual`] = empresaData.inscricaoEstadual; }
      if (empresaData.email !== undefined) { updates[`empresas/${empresaCnpj}/email`] = empresaData.email; }
      if (empresaData.telefone !== undefined) { updates[`empresas/${empresaCnpj}/telefone`] = empresaData.telefone; }
      if (empresaData.site !== undefined) { updates[`empresas/${empresaCnpj}/site`] = empresaData.site; }
      if (empresaData.logradouro !== undefined) { updates[`empresas/${empresaCnpj}/logradouro`] = empresaData.logradouro; }
      if (empresaData.numero !== undefined) { updates[`empresas/${empresaCnpj}/numero`] = empresaData.numero; }
      if (empresaData.complemento !== undefined) { updates[`empresas/${empresaCnpj}/complemento`] = empresaData.complemento; }
      if (empresaData.bairro !== undefined) { updates[`empresas/${empresaCnpj}/bairro`] = empresaData.bairro; }
      if (empresaData.cidade !== undefined) { updates[`empresas/${empresaCnpj}/cidade`] = empresaData.cidade; }
      if (empresaData.cep !== undefined) { updates[`empresas/${empresaCnpj}/cep`] = empresaData.cep; }
      if (empresaData.googlePlaceId !== undefined) { updates[`empresas/${empresaCnpj}/googlePlaceId`] = empresaData.googlePlaceId; }
      if (empresaData.logoUrl !== undefined) { updates[`empresas/${empresaCnpj}/logoUrl`] = empresaData.logoUrl; }
      if (empresaData.status !== undefined) { updates[`empresas/${empresaCnpj}/status`] = empresaData.status; }
      if (empresaData.redesSociais !== undefined) { updates[`empresas/${empresaCnpj}/redesSociais`] = empresaData.redesSociais; }
      if (empresaData.coresTema !== undefined) { updates[`empresas/${empresaCnpj}/coresTema`] = empresaData.coresTema; }

      updates[`empresas/${empresaCnpj}/updateDate`] = updateDate;

      await update(ref(database), updates);
      console.log(`✅ Empresa ${empresaCnpj} atualizada com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar empresa:', error);
      throw error;
    }
  }

  // Métricas do Dashboard
  async getDashboardMetrics(empresaCnpj: string) {
    try {
      const [alunos, professores, turmas, mensalidades] = await Promise.all([
        this.getAlunos(empresaCnpj),
        this.getProfessores(empresaCnpj),
        this.getTurmas(empresaCnpj),
        this.getAllMensalidades(empresaCnpj)
      ]);

      // Calcular métricas
      const alunosAtivos = alunos.length;
      const professoresAtivos = professores.length;
      const turmasAtivas = turmas.filter(t => t.ativo).length;
      
      // Mensalidades do mês atual
      const mesAtual = new Date().getMonth();
      const anoAtual = new Date().getFullYear();
      
      const mensalidadesMesAtual = mensalidades.filter(m => {
        const dataVencimento = new Date(m.dataVencimento);
        return dataVencimento.getMonth() === mesAtual && 
               dataVencimento.getFullYear() === anoAtual;
      });

      const mensalidadesPagas = mensalidadesMesAtual.filter(m => m.dataPagamento && m.dataPagamento !== '');
      const valorRecebido = mensalidadesPagas.reduce((sum, m) => sum + (m.valorPago || m.valor), 0);
      
      const mensalidadesVencidas = mensalidadesMesAtual.filter(m => {
        if (m.dataPagamento && m.dataPagamento !== '') return false;
        const vencimento = new Date(m.dataVencimento);
        return vencimento < new Date();
      });
      
      const valorVencido = mensalidadesVencidas.reduce((sum, m) => sum + m.valor, 0);

      return {
        alunosAtivos,
        professoresAtivos,
        turmasAtivas,
        mensalidadesPagas: mensalidadesPagas.length,
        valorRecebido,
        mensalidadesVencidas: mensalidadesVencidas.length,
        valorVencido,
        totalMensalidades: mensalidadesMesAtual.length
      };
    } catch (error) {
      console.error('Erro ao buscar métricas do dashboard:', error);
      throw error;
    }
  }

  // Dados para gráficos do Dashboard
  async getDashboardCharts(empresaCnpj: string) {
    try {
      const [alunos, mensalidades] = await Promise.all([
        this.getAlunos(empresaCnpj),
        this.getAllMensalidades(empresaCnpj)
      ]);

      // Gráfico de receita e alunos (últimos 6 meses)
      const chartData = [];
      const now = new Date();
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const month = date.getMonth();
        const year = date.getFullYear();
        
        const mensalidadesMes = mensalidades.filter(m => {
          const dataVencimento = new Date(m.dataVencimento);
          return dataVencimento.getMonth() === month && 
                 dataVencimento.getFullYear() === year;
        });
        
        const receita = mensalidadesMes
          .filter(m => m.dataPagamento && m.dataPagamento !== '')
          .reduce((sum, m) => sum + (m.valorPago || m.valor), 0);
        
        const alunosMes = alunos.filter(a => {
          const dataCriacao = new Date(a.createDate);
          return dataCriacao.getMonth() <= month && 
                 dataCriacao.getFullYear() <= year;
        }).length;
        
        chartData.push({
          mes: date.toLocaleDateString('pt-BR', { month: 'short' }),
          receita,
          alunos: alunosMes
        });
      }

      // Gráfico de distribuição por níveis
      const niveis = await this.getNiveis(empresaCnpj);
      const pieData = niveis.map(nivel => {
        const alunosNivel = alunos.filter(a => a.nivelId === nivel.id).length;
        return {
          name: nivel.nome,
          value: alunosNivel,
          color: nivel.cor || '#3B82F6'
        };
      });

      return {
        chartData,
        pieData
      };
    } catch (error) {
      console.error('Erro ao buscar dados dos gráficos:', error);
      throw error;
    }
  }

  // Níveis
  async getNiveis(empresaCnpj: string) {
    try {
      const niveisRef = ref(database, `empresas/${empresaCnpj}/niveis`);
      const snapshot = await get(niveisRef);
      
      if (snapshot.exists()) {
        const niveis: any[] = [];
        snapshot.forEach((childSnapshot) => {
          const nivelData = childSnapshot.val();
          // Converter ativo de número para boolean se necessário
          const ativo = nivelData.ativo !== undefined 
            ? (typeof nivelData.ativo === 'boolean' ? nivelData.ativo : nivelData.ativo === 1 || nivelData.ativo === true)
            : true;
          
          niveis.push({
            id: childSnapshot.key!,
            ...nivelData,
            ativo
          });
        });
        return niveis;
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar níveis:', error);
      throw error;
    }
  }

  // Criar novo nível
  async createNivel(empresaCnpj: string, nivelData: any): Promise<string> {
    try {
      const niveisRef = ref(database, `empresas/${empresaCnpj}/niveis`);
      const newNivelRef = push(niveisRef);
      const nivelId = newNivelRef.key!;
      
      const now = new Date().toISOString();
      const ordenacao = nivelData.ordenacao ?? nivelData.ordem ?? 0;
      
      const nivel: any = {
        id: nivelId,
        nome: nivelData.nome || '',
        descricao: nivelData.descricao || '',
        cor: nivelData.cor || '',
        ordenacao: ordenacao,
        ordem: ordenacao, // Mantido para compatibilidade
        ativo: nivelData.ativo !== undefined ? (nivelData.ativo ? 1 : 0) : 1,
        createDate: now,
        updateDate: now
      };

      if (nivelData.imagem) {
        nivel.imagem = nivelData.imagem;
      }

      if (nivelData.requisitos) {
        nivel.requisitos = nivelData.requisitos;
      }

      await set(newNivelRef, nivel);
      console.log('✅ Nível criado com sucesso:', nivelId);
      return nivelId;
    } catch (error) {
      console.error('❌ Erro ao criar nível:', error);
      throw error;
    }
  }

  // Atualizar nível
  async updateNivel(empresaCnpj: string, nivelId: string, nivelData: any): Promise<void> {
    try {
      const updates: any = {};
      const updateDate = new Date().toISOString();

      if (nivelData.nome !== undefined) {
        updates[`empresas/${empresaCnpj}/niveis/${nivelId}/nome`] = nivelData.nome;
      }
      if (nivelData.descricao !== undefined) {
        updates[`empresas/${empresaCnpj}/niveis/${nivelId}/descricao`] = nivelData.descricao;
      }
      if (nivelData.cor !== undefined) {
        updates[`empresas/${empresaCnpj}/niveis/${nivelId}/cor`] = nivelData.cor;
      }
      if (nivelData.ordenacao !== undefined) {
        updates[`empresas/${empresaCnpj}/niveis/${nivelId}/ordenacao`] = nivelData.ordenacao;
        updates[`empresas/${empresaCnpj}/niveis/${nivelId}/ordem`] = nivelData.ordenacao; // Mantido para compatibilidade
      } else if (nivelData.ordem !== undefined) {
        updates[`empresas/${empresaCnpj}/niveis/${nivelId}/ordenacao`] = nivelData.ordem;
        updates[`empresas/${empresaCnpj}/niveis/${nivelId}/ordem`] = nivelData.ordem;
      }
      if (nivelData.imagem !== undefined) {
        updates[`empresas/${empresaCnpj}/niveis/${nivelId}/imagem`] = nivelData.imagem;
      }
      if (nivelData.requisitos !== undefined) {
        if (nivelData.requisitos === null || (typeof nivelData.requisitos === 'object' && Object.keys(nivelData.requisitos).length === 0)) {
          // Remover requisitos se for null ou objeto vazio
          updates[`empresas/${empresaCnpj}/niveis/${nivelId}/requisitos`] = null;
        } else {
          updates[`empresas/${empresaCnpj}/niveis/${nivelId}/requisitos`] = nivelData.requisitos;
        }
      }
      if (nivelData.ativo !== undefined) {
        // Salvar como número para compatibilidade com Firebase
        updates[`empresas/${empresaCnpj}/niveis/${nivelId}/ativo`] = nivelData.ativo ? 1 : 0;
      }

      updates[`empresas/${empresaCnpj}/niveis/${nivelId}/updateDate`] = updateDate;

      await update(ref(database), updates);
      console.log(`✅ Nível ${nivelId} atualizado com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar nível:', error);
      throw error;
    }
  }

  // Serviços
  async getServicos(empresaCnpj: string) {
    try {
      const servicosRef = ref(database, `empresas/${empresaCnpj}/servicos`);
      const snapshot = await get(servicosRef);
      
      if (snapshot.exists()) {
        const servicos: any[] = [];
        snapshot.forEach((childSnapshot) => {
          const servicoData = childSnapshot.val();
          servicos.push({
            id: childSnapshot.key!,
            ...servicoData
          });
        });
        return servicos;
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar serviços:', error);
      throw error;
    }
  }

  // Função otimizada para carregar dados básicos (turmas e professores)
  async getAllData(empresaCnpj: string) {
    try {
      console.log('🚀 Carregando dados básicos para CNPJ:', empresaCnpj);
      
      // Carregar turmas e usuários em paralelo
      const [turmas, usuarios] = await Promise.all([
        this.getTurmas(empresaCnpj),
        this.getUsuarios(empresaCnpj)
      ]);
      
      // Separar professores dos usuários
      const professores = usuarios.filter(user => user.cargo === 'professor' && user.bAtivo);
      
      console.log('🔍 Debug - Usuários carregados:', usuarios);
      console.log('🔍 Debug - Professores filtrados:', professores);
      console.log('🔍 Debug - Turmas carregadas:', turmas);
      
      console.log('✅ Dados básicos carregados:', {
        turmas: turmas.length,
        usuarios: usuarios.length,
        professores: professores.length
      });
      
      return {
        turmas,
        usuarios,
        professores
      };
    } catch (error) {
      console.error('❌ Erro ao carregar dados básicos:', error);
      throw error;
    }
  }

  // Função otimizada para carregar dados completos (incluindo alunos)
  async getAllDataWithAlunos(empresaCnpj: string) {
    try {
      console.log('🚀 Carregando dados completos para CNPJ:', empresaCnpj);
      
      // Carregar todos os dados em paralelo
      const [turmas, usuarios, alunos] = await Promise.all([
        this.getTurmas(empresaCnpj),
        this.getUsuarios(empresaCnpj),
        this.getAlunos(empresaCnpj)
      ]);
      
      // Separar professores dos usuários
      const professores = usuarios.filter(user => user.cargo === 'professor' && user.bAtivo);
      
      console.log('✅ Dados completos carregados:', {
        turmas: turmas.length,
        usuarios: usuarios.length,
        professores: professores.length,
        alunos: alunos.length
      });
      
      return {
        turmas,
        usuarios,
        professores,
        alunos
      };
    } catch (error) {
      console.error('❌ Erro ao carregar dados completos:', error);
      throw error;
    }
  }

  // Atualizar status do aluno (ativar/inativar)
  async updateAlunoStatus(empresaCnpj: string, alunoId: string, isActive: boolean): Promise<void> {
    try {
      // Atualizar em ambas as localizações (alunos e usuarios)
      const updates: any = {};
      updates[`empresas/${empresaCnpj}/alunos/${alunoId}/bAtivo`] = isActive;
      updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/bAtivo`] = isActive;
      updates[`empresas/${empresaCnpj}/alunos/${alunoId}/updateDate`] = new Date().toISOString();
      updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/updateDate`] = new Date().toISOString();
      
      await update(ref(database), updates);
      console.log(`✅ Status do aluno ${alunoId} atualizado para ${isActive ? 'ativo' : 'inativo'}`);
    } catch (error) {
      console.error('❌ Erro ao atualizar status do aluno:', error);
      throw error;
    }
  }

  // Atualizar dados completos do aluno
  async updateAluno(empresaCnpj: string, alunoId: string, alunoData: Partial<User>): Promise<void> {
    try {
      const updates: any = {};
      const updateDate = new Date().toISOString();

      // Atualizar campos em /alunos
      if (alunoData.nome !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/nome`] = alunoData.nome;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/nome`] = alunoData.nome;
      }
      if (alunoData.email !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/email`] = alunoData.email;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/email`] = alunoData.email;
      }
      if (alunoData.telefone !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/telefone`] = alunoData.telefone;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/telefone`] = alunoData.telefone;
      }
      if (alunoData.cellular !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/cellular`] = alunoData.cellular;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/cellular`] = alunoData.cellular;
      }
      if (alunoData.cpf !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/cpf`] = alunoData.cpf;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/cpf`] = alunoData.cpf;
      }
      if (alunoData.dataNascimento !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/dataNascimento`] = alunoData.dataNascimento;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/dataNascimento`] = alunoData.dataNascimento;
      }
      if (alunoData.fotoUrl !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/fotoUrl`] = alunoData.fotoUrl;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/fotoUrl`] = alunoData.fotoUrl;
      }
      if (alunoData.nivelId !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/nivelId`] = alunoData.nivelId;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/nivelId`] = alunoData.nivelId;
      }
      if (alunoData.nivel !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/nivel`] = alunoData.nivel;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/nivel`] = alunoData.nivel;
      }
      if (alunoData.progressoNivel !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/progressoNivel`] = alunoData.progressoNivel;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/progressoNivel`] = alunoData.progressoNivel;
      }
      if (alunoData.professorId !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/professorId`] = alunoData.professorId;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/professorId`] = alunoData.professorId;
      }
      if (alunoData.turmas !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/turmas`] = alunoData.turmas;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/turmas`] = alunoData.turmas;
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/turmasIds`] = alunoData.turmas;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/turmasIds`] = alunoData.turmas;
      }
      if (alunoData.diaVencimento !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/diaVencimento`] = alunoData.diaVencimento;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/diaVencimento`] = alunoData.diaVencimento;
      }
      if (alunoData.valorMensalidade !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/valorMensalidade`] = alunoData.valorMensalidade;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/valorMensalidade`] = alunoData.valorMensalidade;
      }
      if (alunoData.bAtivo !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/bAtivo`] = alunoData.bAtivo;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/bAtivo`] = alunoData.bAtivo;
      }
      if (alunoData.enderecoId !== undefined) {
        updates[`empresas/${empresaCnpj}/alunos/${alunoId}/enderecoId`] = alunoData.enderecoId;
        updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/enderecoId`] = alunoData.enderecoId;
      }

      // Sempre atualizar updateDate
      updates[`empresas/${empresaCnpj}/alunos/${alunoId}/updateDate`] = updateDate;
      updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/updateDate`] = updateDate;

      await update(ref(database), updates);
      console.log(`✅ Aluno ${alunoId} atualizado com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar aluno:', error);
      throw error;
    }
  }

  // Deletar aluno (soft delete)
  async deleteAluno(empresaCnpj: string, alunoId: string): Promise<void> {
    try {
      const deleteDate = new Date().toISOString();
      const updates: any = {};
      
      // Marcar como deletado em ambas as localizações
      updates[`empresas/${empresaCnpj}/alunos/${alunoId}/deleteDate`] = deleteDate;
      updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/deleteDate`] = deleteDate;
      updates[`empresas/${empresaCnpj}/alunos/${alunoId}/bAtivo`] = false;
      updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/bAtivo`] = false;
      updates[`empresas/${empresaCnpj}/alunos/${alunoId}/updateDate`] = deleteDate;
      updates[`empresas/${empresaCnpj}/usuarios/${alunoId}/updateDate`] = deleteDate;

      await update(ref(database), updates);
      console.log(`✅ Aluno ${alunoId} deletado com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao deletar aluno:', error);
      throw error;
    }
  }

  // Assinaturas
  async getAssinaturas(empresaCnpj: string): Promise<Assinatura[]> {
    try {
      const assinaturasRef = ref(database, `empresas/${empresaCnpj}/assinaturas`);
      const snapshot = await get(assinaturasRef);
      
      if (snapshot.exists()) {
        const assinaturas: Assinatura[] = [];
        snapshot.forEach((childSnapshot) => {
          const assinaturaData = childSnapshot.val();
          assinaturas.push({
            ...assinaturaData,
            id: childSnapshot.key! // ID interno do Firebase deve ter prioridade
          });
        });
        return assinaturas;
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar assinaturas:', error);
      throw error;
    }
  }

  async getAssinatura(assinaturaId: string, empresaCnpj?: string): Promise<Assinatura | null> {
    try {
      console.log('🔍 Buscando assinatura ID:', assinaturaId);
      
      // Se temos o CNPJ da empresa, buscar diretamente
      if (empresaCnpj) {
        const assinaturaRef = ref(database, `empresas/${empresaCnpj}/assinaturas/${assinaturaId}`);
        const snapshot = await get(assinaturaRef);
        
        if (snapshot.exists()) {
          const assinaturaData = snapshot.val();
          console.log('✅ Assinatura encontrada na empresa:', assinaturaData);
          
          return {
            id: assinaturaId,
            empresaId: empresaCnpj,
            ...assinaturaData
          };
        }
      }
      
      // Buscar em todas as empresas (fallback)
      const empresasRef = ref(database, 'empresas');
      const snapshot = await get(empresasRef);
      
      if (snapshot.exists()) {
        const empresas = snapshot.val();
        console.log('🏢 Empresas encontradas:', Object.keys(empresas));
        
        for (const [empresaCnpj, empresaData] of Object.entries(empresas)) {
          if (empresaData && typeof empresaData === 'object' && 'assinaturas' in empresaData) {
            const assinaturas = (empresaData as any).assinaturas;
            console.log(`📋 Assinaturas da empresa ${empresaCnpj}:`, Object.keys(assinaturas || {}));
            
            if (assinaturas && assinaturas[assinaturaId]) {
              const assinaturaData = assinaturas[assinaturaId];
              console.log('✅ Assinatura encontrada:', assinaturaData);
              
              return {
                id: assinaturaId,
                empresaId: empresaCnpj,
                ...assinaturaData
              };
            }
          }
        }
      }
      
      console.log('❌ Assinatura não encontrada em nenhuma empresa');
      return null;
    } catch (error) {
      console.error('❌ Erro ao buscar assinatura:', error);
      throw error;
    }
  }

  async updateAssinaturaStatus(assinaturaId: string, status: string): Promise<void> {
    try {
      // Buscar a assinatura primeiro para encontrar a empresa
      const assinatura = await this.getAssinatura(assinaturaId);
      if (!assinatura) {
        throw new Error('Assinatura não encontrada');
      }

      const updates: any = {};
      updates[`empresas/${assinatura.empresaId}/assinaturas/${assinaturaId}/status`] = status;
      updates[`empresas/${assinatura.empresaId}/assinaturas/${assinaturaId}/updateDate`] = new Date().toISOString();
      
      await update(ref(database), updates);
      console.log(`✅ Status da assinatura ${assinaturaId} atualizado para ${status}`);
    } catch (error) {
      console.error('❌ Erro ao atualizar status da assinatura:', error);
      throw error;
    }
  }

  async createAssinatura(empresaCnpj: string, assinaturaData: any): Promise<string> {
    try {
      const assinaturasRef = ref(database, `empresas/${empresaCnpj}/assinaturas`);
      const newAssinaturaRef = push(assinaturasRef);
      
      const assinaturaId = newAssinaturaRef.key!;
      
      const assinatura = {
        id: assinaturaId,
        alunoId: assinaturaData.alunoId,
        value: assinaturaData.valor,
        description: assinaturaData.descricao,
        billingType: assinaturaData.formaPagamento,
        cycle: assinaturaData.ciclo,
        createDate: new Date().toISOString(),
        deletedDate: assinaturaData.dataFim || null,
        status: 'ACTIVE',
        interest: assinaturaData.jurosMensal || 0,
        fine: assinaturaData.multaPercentual || 0,
        discount: assinaturaData.descontoPercentual || 0,
        observations: assinaturaData.descricao || '',
        nextDueDate: assinaturaData.dataInicio,
        updateDate: new Date().toISOString(),
        asaasSubscriptionId: '',
        customerId: '',
        customerAsaasId: '',
        empresaId: empresaCnpj,
        environment: 'production'
      };
      
      await set(newAssinaturaRef, assinatura);
      console.log(`✅ Assinatura ${assinaturaId} criada com sucesso`);
      return assinaturaId;
    } catch (error) {
      console.error('❌ Erro ao criar assinatura:', error);
      throw error;
    }
  }

  async deleteAssinatura(empresaCnpj: string, assinaturaId: string): Promise<void> {
    try {
      const assinaturaRef = ref(database, `empresas/${empresaCnpj}/assinaturas/${assinaturaId}`);
      await remove(assinaturaRef);
      console.log(`✅ Assinatura ${assinaturaId} excluída com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao excluir assinatura:', error);
      throw error;
    }
  }

  async deletePagamento(empresaCnpj: string, pagamentoId: string): Promise<void> {
    try {
      const pagamentoRef = ref(database, `empresas/${empresaCnpj}/pagamentos/${pagamentoId}`);
      await remove(pagamentoRef);
      console.log(`✅ Pagamento ${pagamentoId} excluído com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao excluir pagamento:', error);
      throw error;
    }
  }

  async deleteMensalidade(empresaCnpj: string, mensalidadeId: string): Promise<void> {
    try {
      // Buscar a mensalidade primeiro para encontrar o caminho correto
      const mensalidadesRef = ref(database, `empresas/${empresaCnpj}/mensalidades`);
      const snapshot = await get(mensalidadesRef);
      
      if (snapshot.exists()) {
        const mensalidadesData = snapshot.val();
        
        // Procurar a mensalidade em todos os anos/meses
        for (const anoMes in mensalidadesData) {
          const anoMesData = mensalidadesData[anoMes];
          for (const alunoId in anoMesData) {
            const alunoData = anoMesData[alunoId];
            for (const mensalidadeKey in alunoData) {
              if (mensalidadeKey === mensalidadeId) {
                // Encontrou a mensalidade, deletar
                const mensalidadeRef = ref(database, `empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}`);
                await remove(mensalidadeRef);
                console.log(`✅ Mensalidade ${mensalidadeId} excluída com sucesso`);
                return;
              }
            }
          }
        }
      }
      
      console.warn(`⚠️ Mensalidade ${mensalidadeId} não encontrada`);
    } catch (error) {
      console.error('❌ Erro ao excluir mensalidade:', error);
      throw error;
    }
  }

  async createPagamento(empresaCnpj: string, pagamentoData: any): Promise<string> {
    try {
      const pagamentosRef = ref(database, `empresas/${empresaCnpj}/pagamentos`);
      const newPagamentoRef = push(pagamentosRef);
      
      const pagamentoId = newPagamentoRef.key!;
      
      const pagamento = {
        id: pagamentoId,
        alunoId: pagamentoData.alunoId,
        valor: pagamentoData.valor,
        dataVencimento: pagamentoData.dataVencimento,
        descricao: pagamentoData.descricao,
        tipoPagamento: pagamentoData.tipoPagamento,
        status: pagamentoData.status,
        createDate: new Date().toISOString(),
        updateDate: new Date().toISOString(),
        externalReference: `REN_${Date.now()}`,
        asaasPaymentId: '',
        invoiceUrl: '',
        bankSlipUrl: '',
        pixCode: '',
        pixUrl: '',
        qrCodeImageUrl: ''
      };
      
      await set(newPagamentoRef, pagamento);
      console.log(`✅ Pagamento ${pagamentoId} criado com sucesso`);
      
      // Criar notificação para administradores sobre novo pagamento
      try {
        const valorFormatado = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(pagamentoData.valor || 0);
        
        await this.criarNotificacaoParaTodosAdmins(empresaCnpj, {
          titulo: 'Novo Pagamento Criado',
          mensagem: `Um novo pagamento de ${valorFormatado} foi criado.`,
          tipo: 'info',
          lida: false,
          link: `/cobrancas`
        });
      } catch (error) {
        console.warn('⚠️ Erro ao criar notificação de novo pagamento:', error);
      }
      
      return pagamentoId;
    } catch (error) {
      console.error('❌ Erro ao criar pagamento:', error);
      throw error;
    }
  }

  async updatePagamento(empresaCnpj: string, pagamentoData: any): Promise<void> {
    try {
      const pagamentoRef = ref(database, `empresas/${empresaCnpj}/pagamentos/${pagamentoData.id}`);
      await set(pagamentoRef, pagamentoData);
      console.log(`✅ Pagamento ${pagamentoData.id} atualizado com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar pagamento:', error);
      throw error;
    }
  }

  async updateAssinatura(empresaCnpj: string, assinaturaData: any): Promise<void> {
    try {
      const assinaturaRef = ref(database, `empresas/${empresaCnpj}/assinaturas/${assinaturaData.id}`);
      await set(assinaturaRef, assinaturaData);
      console.log(`✅ Assinatura ${assinaturaData.id} atualizada com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar assinatura:', error);
      throw error;
    }
  }

  async getMensalidades(empresaCnpj: string, assinaturaId: string): Promise<Mensalidade[]> {
    try {
      // Buscar no caminho correto: empresas->empresaId->mensalidades->aaaa-mm->alunoId->mensalidadeId
      const mensalidadesRef = ref(database, `empresas/${empresaCnpj}/mensalidades`);
      const snapshot = await get(mensalidadesRef);
      
      if (snapshot.exists()) {
        const mensalidadesData = snapshot.val();
        const mensalidades: Mensalidade[] = [];
        
        // Iterar pelos meses (aaaa-mm)
        Object.keys(mensalidadesData).forEach(anoMes => {
          const mesData = mensalidadesData[anoMes];
          
          // Iterar pelos alunos
          Object.keys(mesData).forEach(alunoId => {
            const alunoMensalidades = mesData[alunoId];
            
            // Iterar pelas mensalidades do aluno
            Object.keys(alunoMensalidades).forEach(mensalidadeId => {
              const mensalidade = alunoMensalidades[mensalidadeId];
              
              // Verificar se a mensalidade pertence à assinatura
              if (mensalidade.assinaturaId === assinaturaId) {
                mensalidades.push({
                  id: mensalidadeId,
                  numero: mensalidade.numero || 0,
                  valor: mensalidade.valor || 0,
                  dataVencimento: mensalidade.dataVencimento || '',
                  status: mensalidade.status || 'pendente',
                  dataPagamento: mensalidade.dataPagamento,
                  valorPago: mensalidade.valorPago,
                  assinaturaId: mensalidade.assinaturaId,
                  alunoId: mensalidade.alunoId,
                  createDate: mensalidade.createDate || new Date().toISOString(),
                  updateDate: mensalidade.updateDate || new Date().toISOString()
                });
              }
            });
          });
        });
        
        console.log(`✅ Encontradas ${mensalidades.length} mensalidades para assinatura ${assinaturaId}`);
        return mensalidades;
      }
      
      return [];
    } catch (error) {
      console.error('❌ Erro ao buscar mensalidades:', error);
      throw error;
    }
  }

  async getAllMensalidades(empresaCnpj: string): Promise<Mensalidade[]> {
    try {
      // Buscar todas as mensalidades diretamente
      const mensalidadesRef = ref(database, `empresas/${empresaCnpj}/mensalidades`);
      const snapshot = await get(mensalidadesRef);
      
      if (snapshot.exists()) {
        const mensalidadesData = snapshot.val();
        const mensalidades: Mensalidade[] = [];
        
        // Iterar pelos meses (aaaa-mm)
        Object.keys(mensalidadesData).forEach(anoMes => {
          const mesData = mensalidadesData[anoMes];
          
          // Iterar pelos alunos
          Object.keys(mesData).forEach(alunoId => {
            const alunoMensalidades = mesData[alunoId];
            
            // Iterar pelas mensalidades do aluno
            Object.keys(alunoMensalidades).forEach(mensalidadeId => {
              const mensalidade = alunoMensalidades[mensalidadeId];
              
              mensalidades.push({
                id: mensalidadeId,
                numero: mensalidade.numero || 0,
                valor: mensalidade.valor || 0,
                dataVencimento: mensalidade.dataVencimento || '',
                status: mensalidade.status || 'pendente',
                dataPagamento: mensalidade.dataPagamento,
                valorPago: mensalidade.valorPago,
                assinaturaId: mensalidade.assinaturaId,
                alunoId: mensalidade.alunoId,
                createDate: mensalidade.createDate || new Date().toISOString(),
                updateDate: mensalidade.updateDate || new Date().toISOString(),
                invoiceUrl: mensalidade.invoiceUrl || ''
              });
            });
          });
        });
        
        console.log(`✅ Encontradas ${mensalidades.length} mensalidades no total`);
        return mensalidades;
      }
      
      return [];
    } catch (error) {
      console.error('❌ Erro ao buscar todas as mensalidades:', error);
      throw error;
    }
  }

  async createMensalidades(empresaCnpj: string, assinaturaId: string, assinaturaData: any): Promise<void> {
    try {
      const mensalidadesRef = ref(database, `empresas/${empresaCnpj}/assinaturas/${assinaturaId}/mensalidades`);
      
      // Verificar se já existem mensalidades
      const snapshot = await get(mensalidadesRef);
      if (snapshot.exists()) {
        console.log('📋 Mensalidades já existem para esta assinatura');
        return;
      }

      const mensalidades: any = {};
      const dataInicio = new Date(assinaturaData.createDate);
      const valor = assinaturaData.value;
      const ciclo = assinaturaData.cycle;
      
      // Gerar mensalidades baseadas no ciclo
      let quantidadeMensalidades = 12; // Padrão: 12 meses
      
      if (ciclo === 'WEEKLY') {
        quantidadeMensalidades = 52; // 52 semanas
      } else if (ciclo === 'BIWEEKLY') {
        quantidadeMensalidades = 26; // 26 quinzenas
      } else if (ciclo === 'QUARTERLY') {
        quantidadeMensalidades = 4; // 4 trimestres
      } else if (ciclo === 'SEMIANNUALLY') {
        quantidadeMensalidades = 2; // 2 semestres
      } else if (ciclo === 'ANNUALLY') {
        quantidadeMensalidades = 1; // 1 ano
      }

      for (let i = 0; i < quantidadeMensalidades; i++) {
        const dataVencimento = new Date(dataInicio);
        
        // Calcular data baseada no ciclo
        if (ciclo === 'WEEKLY') {
          dataVencimento.setDate(dataVencimento.getDate() + (i * 7));
        } else if (ciclo === 'BIWEEKLY') {
          dataVencimento.setDate(dataVencimento.getDate() + (i * 14));
        } else if (ciclo === 'MONTHLY') {
          dataVencimento.setMonth(dataVencimento.getMonth() + i);
        } else if (ciclo === 'QUARTERLY') {
          dataVencimento.setMonth(dataVencimento.getMonth() + (i * 3));
        } else if (ciclo === 'SEMIANNUALLY') {
          dataVencimento.setMonth(dataVencimento.getMonth() + (i * 6));
        } else if (ciclo === 'ANNUALLY') {
          dataVencimento.setFullYear(dataVencimento.getFullYear() + i);
        }

        // Determinar status baseado na data
        const hoje = new Date();
        let status: 'pago' | 'pendente' | 'vencido' = 'pendente';
        
        if (dataVencimento < hoje) {
          // Simular alguns pagos e alguns vencidos
          const random = Math.random();
          if (random < 0.3) {
            status = 'pago';
          } else {
            status = 'vencido';
          }
        }

        const mensalidadeId = `mensalidade_${i + 1}`;
        mensalidades[mensalidadeId] = {
          id: mensalidadeId,
          numero: i + 1,
          valor: valor,
          dataVencimento: dataVencimento.toISOString().split('T')[0],
          status: status,
          dataPagamento: status === 'pago' ? dataVencimento.toISOString().split('T')[0] : null,
          valorPago: status === 'pago' ? valor : null,
          assinaturaId: assinaturaId,
          createDate: new Date().toISOString(),
          updateDate: new Date().toISOString()
        };
      }

      await set(mensalidadesRef, mensalidades);
      console.log(`✅ ${quantidadeMensalidades} mensalidades criadas para assinatura ${assinaturaId}`);
    } catch (error) {
      console.error('❌ Erro ao criar mensalidades:', error);
      throw error;
    }
  }

  // Pagamentos
  async getPagamentos(empresaCnpj: string): Promise<Pagamento[]> {
    try {
      const pagamentosRef = ref(database, `empresas/${empresaCnpj}/pagamentos`);
      const snapshot = await get(pagamentosRef);
      
      if (snapshot.exists()) {
        const pagamentos: Pagamento[] = [];
        snapshot.forEach((childSnapshot) => {
          const pagamentoData = childSnapshot.val();
          pagamentos.push({
            ...pagamentoData,
            id: childSnapshot.key! // ID interno do Firebase deve ter prioridade
          });
        });
        return pagamentos;
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar pagamentos:', error);
      throw error;
    }
  }

  async getPagamentosByAssinatura(empresaCnpj: string, asaasSubscriptionId: string): Promise<Pagamento[]> {
    try {
      const pagamentos = await this.getPagamentos(empresaCnpj);
      return pagamentos.filter(pagamento => pagamento.asaasSubscriptionId === asaasSubscriptionId);
    } catch (error) {
      console.error('Erro ao buscar pagamentos por assinatura:', error);
      throw error;
    }
  }

  async updatePagamentoStatus(empresaCnpj: string, pagamento: Pagamento, status: string, valorPago?: number): Promise<void> {
    try {
      const updates: any = {};
      updates[`empresas/${empresaCnpj}/pagamentos/${pagamento.id}/status`] = status;
      updates[`empresas/${empresaCnpj}/pagamentos/${pagamento.id}/updateDate`] = new Date().toISOString();
      
      if (status === 'pago') {
        updates[`empresas/${empresaCnpj}/pagamentos/${pagamento.id}/dataPagamento`] = new Date().toISOString();
        if (valorPago !== undefined) {
          updates[`empresas/${empresaCnpj}/pagamentos/${pagamento.id}/valorPago`] = valorPago;
        }
      }
      
      await update(ref(database), updates);
      console.log(`✅ Status do pagamento ${pagamento.id} atualizado para ${status}`);
      
      // Criar notificação quando pagamento é marcado como pago
      if (status === 'pago') {
        try {
          const valorFormatado = new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }).format(valorPago || pagamento.valor || 0);
          
          await this.criarNotificacaoParaTodosAdmins(empresaCnpj, {
            titulo: 'Pagamento Recebido',
            mensagem: `Pagamento de ${valorFormatado} foi registrado como pago.`,
            tipo: 'success',
            lida: false,
            link: `/cobrancas`
          });
        } catch (error) {
          console.warn('⚠️ Erro ao criar notificação de pagamento recebido:', error);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar status do pagamento:', error);
      throw error;
    }
  }

  // Links de Pagamento
  async getLinks(empresaCnpj: string): Promise<LinkPagamento[]> {
    try {
      const linksRef = ref(database, `empresas/${empresaCnpj}/links`);
      const snapshot = await get(linksRef);
      
      if (snapshot.exists()) {
        const links: LinkPagamento[] = [];
        snapshot.forEach((childSnapshot) => {
          const linkData = childSnapshot.val();
          links.push({
            id: childSnapshot.key!,
            nome: linkData.nome || '',
            url: linkData.url || '',
            valor: linkData.valor || 0,
            ativo: linkData.ativo !== false,
            dataCriacao: linkData.dataCriacao || ''
          });
        });
        return links.filter(link => link.ativo);
      }
      return [];
    } catch (error) {
      console.error('❌ Erro ao buscar links:', error);
      throw error;
    }
  }

  async getAllMensalidadesComLocalizacao(empresaCnpj: string): Promise<Array<Mensalidade & { anoMes: string }>> {
    try {
      const mensalidadesRef = ref(database, `empresas/${empresaCnpj}/mensalidades`);
      const snapshot = await get(mensalidadesRef);
      
      if (snapshot.exists()) {
        const mensalidadesData = snapshot.val();
        const mensalidades: Array<Mensalidade & { anoMes: string }> = [];
        
        // Iterar pelos meses (aaaa-mm)
        Object.keys(mensalidadesData).forEach(anoMes => {
          const mesData = mensalidadesData[anoMes];
          
          // Iterar pelos alunos
          Object.keys(mesData).forEach(alunoId => {
            const alunoMensalidades = mesData[alunoId];
            
            // Iterar pelas mensalidades do aluno
            Object.keys(alunoMensalidades).forEach(mensalidadeId => {
              const mensalidade = alunoMensalidades[mensalidadeId];
              
              mensalidades.push({
                id: mensalidadeId,
                numero: mensalidade.numero || 0,
                valor: mensalidade.valor || 0,
                dataVencimento: mensalidade.dataVencimento || '',
                status: mensalidade.status || 'pendente',
                dataPagamento: mensalidade.dataPagamento,
                valorPago: mensalidade.valorPago,
                assinaturaId: mensalidade.assinaturaId,
                alunoId: mensalidade.alunoId,
                createDate: mensalidade.createDate || new Date().toISOString(),
                updateDate: mensalidade.updateDate || new Date().toISOString(),
                invoiceUrl: mensalidade.invoiceUrl || '',
                anoMes: anoMes
              });
            });
          });
        });
        
        return mensalidades;
      }
      
      return [];
    } catch (error) {
      console.error('❌ Erro ao buscar mensalidades com localização:', error);
      throw error;
    }
  }

  async updateMensalidadeInvoiceUrl(empresaCnpj: string, mensalidadeId: string, anoMes: string, alunoId: string, invoiceUrl: string): Promise<void> {
    try {
      const updates: any = {};
      updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/invoiceUrl`] = invoiceUrl;
      updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/updateDate`] = new Date().toISOString();
      
      await update(ref(database), updates);
      console.log(`✅ InvoiceUrl da mensalidade ${mensalidadeId} atualizado`);
    } catch (error) {
      console.error('❌ Erro ao atualizar invoiceUrl da mensalidade:', error);
      throw error;
    }
  }

  async updateMensalidadesInvoiceUrlBatch(
    empresaCnpj: string, 
    mensalidades: Array<{id: string, anoMes: string, alunoId: string}>, 
    invoiceUrl: string
  ): Promise<{sucesso: number, erro: number}> {
    try {
      const updates: any = {};
      let sucesso = 0;
      let erro = 0;
      
      for (const mensalidade of mensalidades) {
        try {
          updates[`empresas/${empresaCnpj}/mensalidades/${mensalidade.anoMes}/${mensalidade.alunoId}/${mensalidade.id}/invoiceUrl`] = invoiceUrl;
          updates[`empresas/${empresaCnpj}/mensalidades/${mensalidade.anoMes}/${mensalidade.alunoId}/${mensalidade.id}/updateDate`] = new Date().toISOString();
          sucesso++;
        } catch (error) {
          console.error(`❌ Erro ao preparar atualização da mensalidade ${mensalidade.id}:`, error);
          erro++;
        }
      }
      
      if (Object.keys(updates).length > 0) {
        await update(ref(database), updates);
        console.log(`✅ ${sucesso} mensalidades atualizadas com sucesso`);
      }
      
      return { sucesso, erro };
    } catch (error) {
      console.error('❌ Erro ao atualizar mensalidades em massa:', error);
      throw error;
    }
  }

  async updateMensalidadeStatus(
    empresaCnpj: string, 
    mensalidadeId: string, 
    anoMes: string, 
    alunoId: string, 
    status: string, 
    valorPago?: number
  ): Promise<void> {
    try {
      const updates: any = {};
      updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/status`] = status;
      updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/updateDate`] = new Date().toISOString();
      
      if (status === 'pago') {
        updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/dataPagamento`] = new Date().toISOString();
        if (valorPago !== undefined) {
          updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/valorPago`] = valorPago;
        }
      }
      
      await update(ref(database), updates);
      console.log(`✅ Status da mensalidade ${mensalidadeId} atualizado para ${status}`);
    } catch (error) {
      console.error('❌ Erro ao atualizar status da mensalidade:', error);
      throw error;
    }
  }

  async updateMensalidade(
    empresaCnpj: string,
    mensalidadeId: string,
    anoMes: string,
    alunoId: string,
    dados: {
      valor?: number;
      dataVencimento?: string;
      status?: string;
      valorPago?: number;
      dataPagamento?: string;
      tipoPagamento?: string;
    }
  ): Promise<void> {
    try {
      const updates: any = {};
      
      if (dados.valor !== undefined) {
        updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/valor`] = dados.valor;
      }
      if (dados.dataVencimento !== undefined) {
        updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/dataVencimento`] = dados.dataVencimento;
      }
      if (dados.status !== undefined) {
        updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/status`] = dados.status;
      }
      if (dados.valorPago !== undefined) {
        updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/valorPago`] = dados.valorPago;
      }
      if (dados.dataPagamento !== undefined) {
        updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/dataPagamento`] = dados.dataPagamento;
      }
      if (dados.tipoPagamento !== undefined) {
        updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/tipoPagamento`] = dados.tipoPagamento;
      }
      
      updates[`empresas/${empresaCnpj}/mensalidades/${anoMes}/${alunoId}/${mensalidadeId}/updateDate`] = new Date().toISOString();
      
      await update(ref(database), updates);
      console.log(`✅ Mensalidade ${mensalidadeId} atualizada com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar mensalidade:', error);
      throw error;
    }
  }

  // FireAgent - Configurações
  async getFireAgentConfig(empresaCnpj: string): Promise<FireAgentConfig | null> {
    try {
      const configRef = ref(database, `empresas/${empresaCnpj}/fireagent`);
      const snapshot = await get(configRef);
      
      if (snapshot.exists()) {
        return snapshot.val() as FireAgentConfig;
      }
      return null;
    } catch (error) {
      console.error('Erro ao buscar configuração FireAgent:', error);
      throw error;
    }
  }

  async saveFireAgentConfig(empresaCnpj: string, config: FireAgentConfig): Promise<void> {
    try {
      const configRef = ref(database, `empresas/${empresaCnpj}/fireagent`);
      await set(configRef, {
        ...config,
        configuradoEm: new Date().toISOString()
      });
      console.log('✅ Configuração FireAgent salva com sucesso');
    } catch (error) {
      console.error('Erro ao salvar configuração FireAgent:', error);
      throw error;
    }
  }

  async hasFireAgentAccess(empresaCnpj: string): Promise<boolean> {
    try {
      const configRef = ref(database, `empresas/${empresaCnpj}/fireagent`);
      const snapshot = await get(configRef);
      return snapshot.exists();
    } catch (error) {
      console.error('Erro ao verificar acesso FireAgent:', error);
      return false;
    }
  }

  // Meta API - Configurações
  async getMetaApiConfig(empresaCnpj: string): Promise<MetaApiConfig | null> {
    try {
      const configRef = ref(database, `empresas/${empresaCnpj}/metaapi`);
      const snapshot = await get(configRef);
      
      if (snapshot.exists()) {
        return snapshot.val() as MetaApiConfig;
      }
      return null;
    } catch (error) {
      console.error('Erro ao buscar configuração Meta API:', error);
      throw error;
    }
  }

  async saveMetaApiConfig(empresaCnpj: string, config: MetaApiConfig): Promise<void> {
    try {
      const configRef = ref(database, `empresas/${empresaCnpj}/metaapi`);
      await set(configRef, {
        ...config,
        configuradoEm: new Date().toISOString()
      });
      console.log('✅ Configuração Meta API salva com sucesso');
    } catch (error) {
      console.error('Erro ao salvar configuração Meta API:', error);
      throw error;
    }
  }

  async hasMetaApiAccess(empresaCnpj: string): Promise<boolean> {
    try {
      const configRef = ref(database, `empresas/${empresaCnpj}/metaapi`);
      const snapshot = await get(configRef);
      return snapshot.exists();
    } catch (error) {
      console.error('Erro ao verificar acesso Meta API:', error);
      return false;
    }
  }

  // Embedded Signup
  async saveEmbeddedSignupConfig(empresaCnpj: string, config: EmbeddedSignupConfig): Promise<void> {
    try {
      const configRef = ref(database, `empresas/${empresaCnpj}/embeddedSignup`);
      await set(configRef, {
        ...config,
        configuradoEm: new Date().toISOString()
      });
      console.log('✅ Configuração Embedded Signup salva com sucesso');
    } catch (error) {
      console.error('Erro ao salvar configuração Embedded Signup:', error);
      throw error;
    }
  }

  async getEmbeddedSignupConfig(empresaCnpj: string): Promise<EmbeddedSignupConfig | null> {
    try {
      const configRef = ref(database, `empresas/${empresaCnpj}/embeddedSignup`);
      const snapshot = await get(configRef);
      
      if (snapshot.exists()) {
        return snapshot.val() as EmbeddedSignupConfig;
      }
      
      return null;
    } catch (error) {
      console.error('Erro ao buscar configuração Embedded Signup:', error);
      throw error;
    }
  }

  async saveEmbeddedSignupResult(empresaCnpj: string, result: EmbeddedSignupResponse): Promise<void> {
    try {
      // Salvar resultado do Embedded Signup
      const resultRef = ref(database, `empresas/${empresaCnpj}/embeddedSignup/result`);
      await set(resultRef, {
        ...result,
        configuradoEm: new Date().toISOString()
      });

      // Atualizar ou criar MetaApiConfig com os dados recebidos
      const metaApiConfig: MetaApiConfig = {
        ativo: true,
        accessToken: result.access_token,
        phoneNumberId: result.phone_number_id,
        businessAccountId: result.waba_id,
        version: 'v24.0',
        apiType: 'cloud',
        configuradoEm: result.configuradoEm,
        configuradoPor: result.configuradoPor
      };

      await this.saveMetaApiConfig(empresaCnpj, metaApiConfig);
      console.log('✅ Resultado Embedded Signup salvo e MetaApiConfig atualizado');
    } catch (error) {
      console.error('Erro ao salvar resultado Embedded Signup:', error);
      throw error;
    }
  }

  // Parâmetros do Sistema
  async getParametrosSistema(empresaCnpj: string): Promise<ParametrosSistema | null> {
    try {
      const parametrosRef = ref(database, `empresas/${empresaCnpj}/parametros`);
      const snapshot = await get(parametrosRef);
      
      if (snapshot.exists()) {
        return snapshot.val() as ParametrosSistema;
      }
      
      // Retornar valores padrão se não existir
      return {
        diasAtrasoParaCobranca: 1,
        intervaloEntreCobrancas: 1,
        horarioEnvioInicio: '09:00',
        horarioEnvioFim: '18:00'
      };
    } catch (error) {
      console.error('Erro ao buscar parâmetros do sistema:', error);
      throw error;
    }
  }

  async saveParametrosSistema(empresaCnpj: string, parametros: ParametrosSistema): Promise<void> {
    try {
      const parametrosRef = ref(database, `empresas/${empresaCnpj}/parametros`);
      await set(parametrosRef, parametros);
      console.log('✅ Parâmetros do sistema salvos com sucesso');
    } catch (error) {
      console.error('Erro ao salvar parâmetros do sistema:', error);
      throw error;
    }
  }

  // Mensalidades Vencidas
  async getMensalidadesVencidas(empresaCnpj: string, diasAtraso: number = 1): Promise<ClienteComMensalidadeVencida[]> {
    try {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const dataLimite = new Date(hoje);
      dataLimite.setDate(dataLimite.getDate() - diasAtraso);

      const mensalidadesRef = ref(database, `empresas/${empresaCnpj}/mensalidades`);
      const snapshot = await get(mensalidadesRef);
      
      const clientesVencidos: ClienteComMensalidadeVencida[] = [];
      
      if (!snapshot.exists()) {
        return clientesVencidos;
      }

      // Buscar todos os alunos para obter dados completos
      const alunosRef = ref(database, `empresas/${empresaCnpj}/alunos`);
      const alunosSnapshot = await get(alunosRef);
      const alunosMap = new Map<string, any>();
      
      if (alunosSnapshot.exists()) {
        alunosSnapshot.forEach((alunoSnapshot) => {
          alunosMap.set(alunoSnapshot.key!, alunoSnapshot.val());
        });
      }

      // Iterar sobre todas as mensalidades
      snapshot.forEach((anoMesSnapshot) => {
        const anoMes = anoMesSnapshot.key!;
        anoMesSnapshot.forEach((alunoSnapshot) => {
          const alunoId = alunoSnapshot.key!;
          const aluno = alunosMap.get(alunoId);
          
          if (!aluno) return;

          alunoSnapshot.forEach((mensalidadeSnapshot) => {
            const mensalidade = mensalidadeSnapshot.val() as Mensalidade;
            const mensalidadeId = mensalidadeSnapshot.key!;
            
            // Verificar se está vencida
            const dataVencimento = new Date(mensalidade.dataVencimento);
            dataVencimento.setHours(0, 0, 0, 0);
            
            const status = mensalidade.status || 'pendente';
            const isVencida = dataVencimento < dataLimite && (status === 'pendente' || status === 'vencido');
            
            if (isVencida) {
              const diasAtrasoCalculado = Math.floor((hoje.getTime() - dataVencimento.getTime()) / (1000 * 60 * 60 * 24));
              
              clientesVencidos.push({
                alunoId: alunoId,
                alunoNome: aluno.nome || 'Sem nome',
                alunoTelefone: aluno.cellular || aluno.telefone || '',
                alunoEmail: aluno.email,
                mensalidadeId: mensalidadeId,
                valor: mensalidade.valor,
                dataVencimento: mensalidade.dataVencimento,
                diasAtraso: diasAtrasoCalculado,
                status: status,
                invoiceUrl: mensalidade.invoiceUrl
              });
            }
          });
        });
      });

      // Ordenar por dias de atraso (maior primeiro)
      clientesVencidos.sort((a, b) => b.diasAtraso - a.diasAtraso);

      return clientesVencidos;
    } catch (error) {
      console.error('Erro ao buscar mensalidades vencidas:', error);
      throw error;
    }
  }

  // Criar aluno rapidamente
  async createAluno(empresaCnpj: string, alunoData: Partial<User>): Promise<string> {
    try {
      const alunosRef = ref(database, `empresas/${empresaCnpj}/alunos`);
      const newAlunoRef = push(alunosRef);
      const alunoId = newAlunoRef.key!;
      
      const aluno: User = {
        id: alunoId,
        createDate: new Date().toISOString(),
        updateDate: new Date().toISOString(),
        deleteDate: '',
        enderecoId: alunoData.enderecoId || '',
        dataNascimento: alunoData.dataNascimento || '',
        nome: alunoData.nome || '',
        email: alunoData.email || '',
        telefone: alunoData.telefone || '',
        cellular: alunoData.cellular || alunoData.telefone || '',
        fotoUrl: alunoData.fotoUrl || '',
        perfilId: '',
        lastLogin: '',
        empresaId: empresaCnpj,
        nivelId: alunoData.nivelId || '',
        nivel: alunoData.nivel || '',
        progressoNivel: alunoData.progressoNivel || 0,
        isOnline: false,
        cargo: 'aluno',
        cpf: alunoData.cpf || '',
        turmas: alunoData.turmas || [],
        professorId: alunoData.professorId || '',
        diaVencimento: alunoData.diaVencimento || 10,
        valorMensalidade: alunoData.valorMensalidade || 0,
        bAtivo: alunoData.bAtivo !== undefined ? alunoData.bAtivo : true,
        customerId: alunoData.customerId || ''
      };

      // Criar em ambas as localizações (alunos e usuarios)
      const updates: any = {};
      updates[`empresas/${empresaCnpj}/alunos/${alunoId}`] = aluno;
      updates[`empresas/${empresaCnpj}/usuarios/${alunoId}`] = aluno;

      await update(ref(database), updates);
      console.log('✅ Aluno criado com sucesso:', alunoId);
      
      // Criar notificação para todos os administradores
      try {
        await this.criarNotificacaoParaTodosAdmins(empresaCnpj, {
          titulo: 'Novo Aluno Cadastrado',
          mensagem: `${aluno.nome} foi cadastrado com sucesso no sistema.`,
          tipo: 'success',
          lida: false,
          link: `/alunos`
        });
      } catch (error) {
        console.warn('⚠️ Erro ao criar notificação de novo aluno:', error);
        // Não falhar a criação do aluno se a notificação falhar
      }
      
      return alunoId;
    } catch (error) {
      console.error('Erro ao criar aluno:', error);
      throw error;
    }
  }

  async getAvaliacoes(empresaCnpj: string) {
    try {
      const avaliacoesRef = ref(database, `empresas/${empresaCnpj}/avaliacoes`);
      const snapshot = await get(avaliacoesRef);
      
      if (snapshot.exists()) {
        const avaliacoes: any[] = [];
        snapshot.forEach((childSnapshot) => {
          const avaliacaoData = childSnapshot.val();
          avaliacoes.push({
            id: childSnapshot.key!,
            ...avaliacaoData
          });
        });
        return avaliacoes;
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar avaliações:', error);
      throw error;
    }
  }

  async getAvaliacoesComDetalhes(empresaCnpj: string) {
    try {
      const [avaliacoes, alunos, professores, niveis] = await Promise.all([
        this.getAvaliacoes(empresaCnpj),
        this.getAlunos(empresaCnpj),
        this.getProfessores(empresaCnpj),
        this.getNiveis(empresaCnpj)
      ]);

      return avaliacoes.map(avaliacao => {
        const aluno = alunos.find(a => a.id === avaliacao.alunoId);
        const professor = professores.find(p => p.id === avaliacao.professorId);
        const nivel = niveis.find(n => n.id === avaliacao.nivelId);
        const proximoNivel = avaliacao.proximoNivelId ? niveis.find(n => n.id === avaliacao.proximoNivelId) : null;

        return {
          ...avaliacao,
          aluno: aluno ? {
            id: aluno.id,
            nome: aluno.nome,
            email: aluno.email,
            fotoUrl: aluno.fotoUrl
          } : null,
          professor: professor ? {
            id: professor.id,
            nome: professor.nome,
            email: professor.email
          } : null,
          nivel: nivel ? {
            id: nivel.id,
            nome: nivel.nome
          } : null,
          proximoNivel: proximoNivel ? {
            id: proximoNivel.id,
            nome: proximoNivel.nome
          } : null
        };
      });
    } catch (error) {
      console.error('Erro ao buscar avaliações com detalhes:', error);
      throw error;
    }
  }

  // ========== PERFIS DE ACESSO ==========
  
  async getPerfisAcesso(empresaCnpj: string): Promise<PerfilAcesso[]> {
    try {
      const perfisRef = ref(database, `empresas/${empresaCnpj}/perfis_acesso`);
      const snapshot = await get(perfisRef);
      
      if (snapshot.exists()) {
        const perfis: PerfilAcesso[] = [];
        snapshot.forEach((childSnapshot) => {
          const perfilData = childSnapshot.val();
          perfis.push({
            id: childSnapshot.key!,
            ...perfilData
          });
        });
        return perfis;
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar perfis de acesso:', error);
      throw error;
    }
  }

  async getPerfilAcesso(empresaCnpj: string, perfilId: string): Promise<PerfilAcesso | null> {
    try {
      const perfilRef = ref(database, `empresas/${empresaCnpj}/perfis_acesso/${perfilId}`);
      const snapshot = await get(perfilRef);
      
      if (snapshot.exists()) {
        return {
          id: perfilId,
          ...snapshot.val()
        };
      }
      return null;
    } catch (error) {
      console.error('Erro ao buscar perfil de acesso:', error);
      throw error;
    }
  }

  async createPerfilAcesso(empresaCnpj: string, perfilData: Partial<PerfilAcesso>): Promise<string> {
    try {
      const perfisRef = ref(database, `empresas/${empresaCnpj}/perfis_acesso`);
      const newPerfilRef = push(perfisRef);
      const perfilId = newPerfilRef.key!;
      
      const now = new Date().toISOString();
      
      // Limpar permissões antes de salvar
      const permissoesLimpas = this.cleanPermissoes(perfilData.permissoes || { menus: {}, modulos: {} });
      
      const perfil: PerfilAcesso = {
        id: perfilId,
        nome: perfilData.nome || '',
        descricao: perfilData.descricao || '',
        cargoBase: perfilData.cargoBase || '',
        permissoes: permissoesLimpas,
        ativo: perfilData.ativo !== undefined ? perfilData.ativo : true,
        createDate: now,
        updateDate: now
      };

      console.log('💾 Criando perfil com permissões limpas:', permissoesLimpas);

      await set(newPerfilRef, perfil);
      console.log('✅ Perfil de acesso criado com sucesso:', perfilId);
      return perfilId;
    } catch (error) {
      console.error('❌ Erro ao criar perfil de acesso:', error);
      throw error;
    }
  }

  // Função auxiliar para limpar permissões removendo chaves vazias ou inválidas
  private cleanPermissoes(permissoes: any): any {
    if (!permissoes) {
      return { menus: {}, modulos: {} };
    }

    const cleaned: any = {
      menus: {},
      modulos: {}
    };

    // Limpar menus
    if (permissoes.menus && typeof permissoes.menus === 'object') {
      Object.keys(permissoes.menus).forEach(key => {
        // Remover chaves vazias ou inválidas
        if (key && key.trim() !== '' && !key.match(/[\/\[\]#\$\.]/)) {
          const menuData = permissoes.menus[key];
          if (menuData) {
            cleaned.menus[key] = {
              acesso: menuData.acesso || false,
              submenus: {},
              ...(menuData.path && { path: menuData.path })
            };

            // Limpar submenus
            if (menuData.submenus && typeof menuData.submenus === 'object') {
              Object.keys(menuData.submenus).forEach(subKey => {
                if (subKey && subKey.trim() !== '' && !subKey.match(/[\/\[\]#\$\.]/)) {
                  const submenuData = menuData.submenus[subKey];
                  if (submenuData) {
                    cleaned.menus[key].submenus[subKey] = {
                      acesso: submenuData.acesso || false,
                      ...(submenuData.path && { path: submenuData.path })
                    };
                  }
                }
              });
            }
          }
        }
      });
    }

    // Limpar módulos
    if (permissoes.modulos && typeof permissoes.modulos === 'object') {
      Object.keys(permissoes.modulos).forEach(key => {
        if (key && key.trim() !== '') {
          const moduloData = permissoes.modulos[key];
          if (moduloData && typeof moduloData === 'object') {
            cleaned.modulos[key] = {
              criar: moduloData.criar || false,
              editar: moduloData.editar || false,
              excluir: moduloData.excluir || false,
              visualizar: moduloData.visualizar || false
            };
          }
        }
      });
    }

    return cleaned;
  }

  async updatePerfilAcesso(empresaCnpj: string, perfilId: string, perfilData: Partial<PerfilAcesso>): Promise<void> {
    try {
      const updates: any = {};
      const updateDate = new Date().toISOString();

      if (perfilData.nome !== undefined) { updates[`empresas/${empresaCnpj}/perfis_acesso/${perfilId}/nome`] = perfilData.nome; }
      if (perfilData.descricao !== undefined) { updates[`empresas/${empresaCnpj}/perfis_acesso/${perfilId}/descricao`] = perfilData.descricao; }
      if (perfilData.cargoBase !== undefined) { updates[`empresas/${empresaCnpj}/perfis_acesso/${perfilId}/cargoBase`] = perfilData.cargoBase; }
      if (perfilData.permissoes !== undefined) { 
        // Limpar permissões antes de salvar
        const permissoesLimpas = this.cleanPermissoes(perfilData.permissoes);
        updates[`empresas/${empresaCnpj}/perfis_acesso/${perfilId}/permissoes`] = permissoesLimpas;
      }
      if (perfilData.ativo !== undefined) { updates[`empresas/${empresaCnpj}/perfis_acesso/${perfilId}/ativo`] = perfilData.ativo; }

      updates[`empresas/${empresaCnpj}/perfis_acesso/${perfilId}/updateDate`] = updateDate;

      console.log('💾 Atualizando perfil com dados limpos:', updates);
      
      await update(ref(database), updates);
      console.log(`✅ Perfil de acesso ${perfilId} atualizado com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar perfil de acesso:', error);
      throw error;
    }
  }

  async deletePerfilAcesso(empresaCnpj: string, perfilId: string): Promise<void> {
    try {
      const perfilRef = ref(database, `empresas/${empresaCnpj}/perfis_acesso/${perfilId}`);
      await remove(perfilRef);
      console.log(`✅ Perfil de acesso ${perfilId} removido com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao remover perfil de acesso:', error);
      throw error;
    }
  }

  // ========== USUÁRIOS ADMINISTRATIVOS ==========

  async createUsuario(empresaCnpj: string, usuarioData: Partial<User> & { password?: string }): Promise<string> {
    try {
      if (!usuarioData.email || !usuarioData.password) {
        throw new Error('Email e senha são obrigatórios');
      }

      console.log('🔐 Criando usuário no Firebase Auth...', { email: usuarioData.email, empresaCnpj });
      
      // Salvar o usuário admin atual antes de criar o novo usuário
      const adminUser = auth.currentUser;
      if (!adminUser) {
        throw new Error('Nenhum usuário autenticado. Faça login novamente.');
      }
      
      const adminUid = adminUser.uid;
      console.log('👤 Admin autenticado:', adminUid, adminUser.email);
      
      // Criar usuário no Firebase Auth
      // NOTA: createUserWithEmailAndPassword não faz login automático, mas pode mudar o contexto
      const userCredential = await createUserWithEmailAndPassword(auth, usuarioData.email, usuarioData.password);
      const firebaseUserId = userCredential.user.uid;
      
      console.log('✅ Usuário criado no Auth:', firebaseUserId);
      console.log('👤 Usuário autenticado após criação:', auth.currentUser?.uid);
      
      // Se o contexto mudou, precisamos restaurar o admin
      // Mas como não temos a senha do admin, vamos usar uma abordagem diferente:
      // Vamos criar o objeto de dados sem campos undefined e salvar
      
      // Criar registro no Realtime Database
      const usuariosRef = ref(database, `empresas/${empresaCnpj}/usuarios/${firebaseUserId}`);
      const now = new Date().toISOString();
      
      // Construir objeto de usuário removendo campos undefined
      const usuario: any = {
        id: firebaseUserId,
        createDate: now,
        updateDate: now,
        deleteDate: '',
        enderecoId: usuarioData.enderecoId || '',
        dataNascimento: usuarioData.dataNascimento || '',
        nome: usuarioData.nome || '',
        email: usuarioData.email,
        telefone: usuarioData.telefone || '',
        cellular: usuarioData.cellular || usuarioData.telefone || '',
        fotoUrl: usuarioData.fotoUrl || '',
        perfilId: '',
        lastLogin: '',
        empresaId: empresaCnpj,
        nivelId: '',
        nivel: '',
        progressoNivel: 0,
        isOnline: false,
        cargo: usuarioData.cargo || 'administrador',
        cpf: usuarioData.cpf || '',
        turmas: usuarioData.turmas || [],
        professorId: '',
        diaVencimento: 0,
        valorMensalidade: 0,
        bAtivo: usuarioData.bAtivo !== undefined ? usuarioData.bAtivo : true,
        customerId: ''
      };

      // Adicionar campos opcionais apenas se tiverem valores válidos (não undefined)
      if (usuarioData.perfilAcessoId) {
        usuario.perfilAcessoId = usuarioData.perfilAcessoId;
      }
      
      if (usuarioData.permissoesCustomizadas) {
        usuario.permissoesCustomizadas = usuarioData.permissoesCustomizadas;
      }

      console.log('💾 Tentando salvar no Realtime Database...', { path: `empresas/${empresaCnpj}/usuarios/${firebaseUserId}` });
      console.log('📋 Dados do usuário (sem campos undefined):', usuario);
      console.log('🔑 Admin UID (deve estar autenticado):', auth.currentUser?.uid);
      
      await set(usuariosRef, usuario);
      console.log('✅ Usuário criado com sucesso no Realtime Database:', firebaseUserId);
      
      // Criar notificação para o usuário criado (se não for o próprio admin que criou)
      try {
        if (adminUid !== firebaseUserId) {
          await this.criarNotificacao(empresaCnpj, firebaseUserId, {
            titulo: 'Bem-vindo ao Sistema!',
            mensagem: `Sua conta foi criada com sucesso. Você pode acessar o painel administrativo.`,
            tipo: 'success',
            lida: false,
            link: '/'
          });
        }
        
        // Notificar outros administradores sobre o novo usuário
        await this.criarNotificacaoParaTodosAdmins(empresaCnpj, {
          titulo: 'Novo Usuário Criado',
          mensagem: `${usuario.nome} (${usuario.email}) foi adicionado ao sistema como ${usuario.cargo}.`,
          tipo: 'info',
          lida: false,
          link: '/configuracoes/usuarios'
        });
      } catch (error) {
        console.warn('⚠️ Erro ao criar notificações de novo usuário:', error);
        // Não falhar a criação do usuário se a notificação falhar
      }
      
      return firebaseUserId;
    } catch (error: any) {
      console.error('❌ Erro ao criar usuário:', error);
      console.error('📊 Detalhes do erro:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Se o erro for de permissão, fornecer uma mensagem mais clara
      if (error.code === 'PERMISSION_DENIED' || error.message?.includes('permission')) {
        throw new Error('Permissão negada. Verifique se você tem permissão de administrador para criar usuários.');
      }
      
      throw error;
    }
  }

  async updateUsuario(empresaCnpj: string, usuarioId: string, usuarioData: Partial<User> & { password?: string }): Promise<void> {
    try {
      const updates: any = {};
      const updateDate = new Date().toISOString();

      // Atualizar campos do usuário
      if (usuarioData.nome !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/nome`] = usuarioData.nome; }
      if (usuarioData.email !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/email`] = usuarioData.email; }
      if (usuarioData.telefone !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/telefone`] = usuarioData.telefone; }
      if (usuarioData.cellular !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/cellular`] = usuarioData.cellular; }
      if (usuarioData.fotoUrl !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/fotoUrl`] = usuarioData.fotoUrl; }
      if (usuarioData.cargo !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/cargo`] = usuarioData.cargo; }
      if (usuarioData.cpf !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/cpf`] = usuarioData.cpf; }
      if (usuarioData.bAtivo !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/bAtivo`] = usuarioData.bAtivo; }
      if (usuarioData.perfilAcessoId !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/perfilAcessoId`] = usuarioData.perfilAcessoId; }
      if (usuarioData.permissoesCustomizadas !== undefined) { updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/permissoesCustomizadas`] = usuarioData.permissoesCustomizadas; }

      updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/updateDate`] = updateDate;

      await update(ref(database), updates);

      // Atualizar senha se fornecida
      if (usuarioData.password) {
        // Nota: Para atualizar senha, o usuário precisa estar autenticado
        // Isso deve ser feito através de um fluxo separado de redefinição de senha
        console.log('⚠️ Atualização de senha deve ser feita pelo próprio usuário ou através de redefinição');
      }

      console.log(`✅ Usuário ${usuarioId} atualizado com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao atualizar usuário:', error);
      throw error;
    }
  }

  async inativarUsuario(empresaCnpj: string, usuarioId: string): Promise<void> {
    try {
      const updates: any = {};
      const updateDate = new Date().toISOString();

      updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/bAtivo`] = false;
      updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/updateDate`] = updateDate;

      await update(ref(database), updates);
      console.log(`✅ Usuário ${usuarioId} inativado com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao inativar usuário:', error);
      throw error;
    }
  }

  async ativarUsuario(empresaCnpj: string, usuarioId: string): Promise<void> {
    try {
      const updates: any = {};
      const updateDate = new Date().toISOString();

      updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/bAtivo`] = true;
      updates[`empresas/${empresaCnpj}/usuarios/${usuarioId}/updateDate`] = updateDate;

      await update(ref(database), updates);
      console.log(`✅ Usuário ${usuarioId} ativado com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao ativar usuário:', error);
      throw error;
    }
  }

  async getPermissoesUsuario(empresaCnpj: string, usuarioId: string): Promise<Permissoes | null> {
    try {
      // Buscar usuário
      const usuarioRef = ref(database, `empresas/${empresaCnpj}/usuarios/${usuarioId}`);
      const usuarioSnapshot = await get(usuarioRef);
      
      if (!usuarioSnapshot.exists()) {
        return null;
      }

      const usuario = usuarioSnapshot.val() as User;

      // Se tem permissões customizadas, retornar elas
      if (usuario.permissoesCustomizadas) {
        return usuario.permissoesCustomizadas;
      }

      // Se tem perfil de acesso, buscar permissões do perfil
      if (usuario.perfilAcessoId) {
        const perfil = await this.getPerfilAcesso(empresaCnpj, usuario.perfilAcessoId);
        if (perfil && perfil.ativo) {
          return perfil.permissoes;
        }
      }

      // Retornar permissões vazias se não houver perfil
      return { menus: {}, modulos: {} };
    } catch (error) {
      console.error('❌ Erro ao buscar permissões do usuário:', error);
      throw error;
    }
  }

  // Notificações do Sistema
  async getNotificacoesUsuario(empresaCnpj: string, usuarioId: string): Promise<SistemaNotificacao[]> {
    try {
      const notificacoesRef = ref(database, `empresas/${empresaCnpj}/notificacoes_sistema/${usuarioId}`);
      const snapshot = await get(notificacoesRef);
      
      if (!snapshot.exists()) {
        return [];
      }

      const notificacoes: SistemaNotificacao[] = [];
      snapshot.forEach((childSnapshot) => {
        const notificacao = childSnapshot.val();
        notificacoes.push({
          id: childSnapshot.key!,
          ...notificacao
        });
      });

      // Ordenar por data de criação (mais recentes primeiro)
      return notificacoes.sort((a, b) => 
        new Date(b.dataCriacao).getTime() - new Date(a.dataCriacao).getTime()
      );
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
      throw error;
    }
  }

  async criarNotificacao(
    empresaCnpj: string,
    usuarioId: string,
    notificacaoData: Omit<SistemaNotificacao, 'id' | 'dataCriacao' | 'empresaCnpj' | 'usuarioId'>
  ): Promise<string> {
    try {
      const notificacoesRef = ref(database, `empresas/${empresaCnpj}/notificacoes_sistema/${usuarioId}`);
      const novaNotificacaoRef = push(notificacoesRef);
      
      const notificacao: SistemaNotificacao = {
        id: novaNotificacaoRef.key!,
        empresaCnpj,
        usuarioId,
        ...notificacaoData,
        dataCriacao: new Date().toISOString()
      };

      await set(novaNotificacaoRef, notificacao);
      return novaNotificacaoRef.key!;
    } catch (error) {
      console.error('Erro ao criar notificação:', error);
      throw error;
    }
  }

  async criarNotificacaoParaTodosAdmins(
    empresaCnpj: string,
    notificacaoData: Omit<SistemaNotificacao, 'id' | 'dataCriacao' | 'usuarioId' | 'empresaCnpj'>
  ): Promise<void> {
    try {
      const administradores = await this.getAdministradores(empresaCnpj);
      
      const promises = administradores.map(admin => 
        this.criarNotificacao(empresaCnpj, admin.id, notificacaoData)
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('Erro ao criar notificações para administradores:', error);
      throw error;
    }
  }

  async marcarNotificacaoComoLida(
    empresaCnpj: string,
    usuarioId: string,
    notificacaoId: string
  ): Promise<void> {
    try {
      const notificacaoRef = ref(database, `empresas/${empresaCnpj}/notificacoes_sistema/${usuarioId}/${notificacaoId}`);
      await update(notificacaoRef, {
        lida: true,
        dataLeitura: new Date().toISOString()
      });
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      throw error;
    }
  }

  async marcarTodasComoLidas(empresaCnpj: string, usuarioId: string): Promise<void> {
    try {
      const notificacoesRef = ref(database, `empresas/${empresaCnpj}/notificacoes_sistema/${usuarioId}`);
      const snapshot = await get(notificacoesRef);
      
      if (!snapshot.exists()) {
        return;
      }

      const updates: any = {};
      snapshot.forEach((childSnapshot) => {
        const notificacao = childSnapshot.val();
        if (!notificacao.lida) {
          updates[`${childSnapshot.key}/lida`] = true;
          updates[`${childSnapshot.key}/dataLeitura`] = new Date().toISOString();
        }
      });

      if (Object.keys(updates).length > 0) {
        await update(notificacoesRef, updates);
      }
    } catch (error) {
      console.error('Erro ao marcar todas as notificações como lidas:', error);
      throw error;
    }
  }

  async deletarNotificacao(
    empresaCnpj: string,
    usuarioId: string,
    notificacaoId: string
  ): Promise<void> {
    try {
      const notificacaoRef = ref(database, `empresas/${empresaCnpj}/notificacoes_sistema/${usuarioId}/${notificacaoId}`);
      await remove(notificacaoRef);
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
      throw error;
    }
  }

  async getCargos(): Promise<Array<{ id: string; nome: string; descricao?: string }>> {
    try {
      // Buscar cargos na raiz do Firebase
      const cargosRef = ref(database, 'cargos');
      const snapshot = await get(cargosRef);
      
      console.log('🔍 Buscando cargos em: cargos/');
      console.log('📊 Snapshot existe:', snapshot.exists());
      
      if (snapshot.exists()) {
        const cargosMap = new Map<string, { id: string; nome: string; descricao?: string }>();
        
        snapshot.forEach((childSnapshot) => {
          const cargoData = childSnapshot.val();
          const cargoId = childSnapshot.key!;
          
          // Verificar estrutura dos dados
          console.log(`📦 Cargo encontrado - ID: ${cargoId}, Dados:`, cargoData);
          
          // Usar o ID como chave para evitar duplicatas
          if (!cargosMap.has(cargoId)) {
            // Tentar diferentes campos possíveis para o nome
            const nome = cargoData.nome || 
                        cargoData.descricao || 
                        cargoData.name || 
                        cargoData.title ||
                        cargoId;
            
            cargosMap.set(cargoId, {
              id: cargoId,
              nome: nome,
              descricao: cargoData.descricao || cargoData.description
            });
          } else {
            console.warn(`⚠️ Cargo duplicado ignorado: ${cargoId}`);
          }
        });
        
        // Converter Map para Array e ordenar por nome
        const cargos = Array.from(cargosMap.values()).sort((a, b) => 
          a.nome.localeCompare(b.nome, 'pt-BR')
        );
        
        console.log(`✅ Total de cargos únicos encontrados: ${cargos.length}`);
        return cargos;
      }
      
      console.log('⚠️ Nenhum cargo encontrado na tabela cargos');
      return [];
    } catch (error) {
      console.error('❌ Erro ao buscar cargos:', error);
      throw error;
    }
  }

  // Dados para Relatórios
  async getRelatoriosData(
    empresaCnpj: string,
    startDate: Date = new Date(new Date().setMonth(new Date().getMonth() - 6)),
    endDate: Date = new Date()
  ) {
    try {
      const [alunos, turmas, mensalidades, niveis, pagamentos] = await Promise.all([
        this.getAlunos(empresaCnpj),
        this.getTurmas(empresaCnpj),
        this.getAllMensalidades(empresaCnpj),
        this.getNiveis(empresaCnpj),
        this.getPagamentos(empresaCnpj)
      ]);

      // Filtrar mensalidades pelo período
      const mensalidadesPeriodo = mensalidades.filter(m => {
        if (!m.dataVencimento) return false;
        const dataVenc = new Date(m.dataVencimento);
        return dataVenc >= startDate && dataVenc <= endDate;
      });

      // Filtrar alunos pelo período (criados até o final do período)
      const alunosPeriodo = alunos.filter(a => {
        if (!a.createDate) return false;
        const dataCriacao = new Date(a.createDate);
        return dataCriacao <= endDate;
      });

      // Calcular período anterior para comparação (mesmo tamanho do período selecionado, antes do início)
      const duracaoPeriodo = endDate.getTime() - startDate.getTime();
      const periodoAnteriorStart = new Date(startDate.getTime() - duracaoPeriodo);
      const periodoAnteriorEnd = new Date(startDate.getTime() - 1);

      // Mensalidades do período anterior
      const mensalidadesPeriodoAnterior = mensalidades.filter((m: Mensalidade) => {
        if (!m.dataVencimento) return false;
        const dataVenc = new Date(m.dataVencimento);
        return dataVenc >= periodoAnteriorStart && dataVenc <= periodoAnteriorEnd;
      });

      // Calcular receita total (todas as mensalidades pagas no período)
      const mensalidadesPagas = mensalidadesPeriodo.filter((m: Mensalidade) => 
        m.dataPagamento && m.dataPagamento !== '' && m.status === 'pago'
      );
      const receitaTotal = mensalidadesPagas.reduce((sum: number, m: Mensalidade) => sum + (m.valorPago || m.valor || 0), 0);

      // Receita do período atual (já calculada acima como receitaTotal)
      const receitaPeriodoAtual = receitaTotal;

      // Receita do período anterior (para comparação)
      const receitaPeriodoAnterior = mensalidadesPeriodoAnterior
        .filter((m: Mensalidade) => m.dataPagamento && m.dataPagamento !== '' && m.status === 'pago')
        .reduce((sum: number, m: Mensalidade) => sum + (m.valorPago || m.valor || 0), 0);

      const variacaoReceita = receitaPeriodoAnterior > 0 
        ? ((receitaPeriodoAtual - receitaPeriodoAnterior) / receitaPeriodoAnterior) * 100 
        : 0;

      // Total de alunos (ativos até o final do período)
      const totalAlunos = alunosPeriodo.filter(a => a.bAtivo !== false).length;
      
      // Alunos no final do período atual
      const alunosFimPeriodo = alunosPeriodo.filter(a => {
        if (!a.createDate) return false;
        const dataCriacao = new Date(a.createDate);
        return dataCriacao <= endDate;
      }).length;
      
      // Alunos no final do período anterior
      const alunosFimPeriodoAnterior = alunos.filter(a => {
        if (!a.createDate) return false;
        const dataCriacao = new Date(a.createDate);
        return dataCriacao <= periodoAnteriorEnd;
      }).length;
      const variacaoAlunos = alunosFimPeriodoAnterior > 0 
        ? ((alunosFimPeriodo - alunosFimPeriodoAnterior) / alunosFimPeriodoAnterior) * 100 
        : 0;

      // Taxa de ocupação (alunos ativos / capacidade total das turmas)
      const turmasAtivas = turmas.filter(t => t.ativo !== false);
      const capacidadeTotal = turmasAtivas.reduce((sum, t) => sum + (t.limiteAlunos || 0), 0);
      const taxaOcupacao = capacidadeTotal > 0 ? (totalAlunos / capacidadeTotal) * 100 : 0;
      const taxaOcupacaoAnterior = taxaOcupacao * 0.97; // Aproximação
      const variacaoOcupacao = taxaOcupacaoAnterior > 0 
        ? ((taxaOcupacao - taxaOcupacaoAnterior) / taxaOcupacaoAnterior) * 100 
        : 0;

      // Aulas realizadas (aproximação baseada em turmas e dias da semana)
      const aulasRealizadas = turmasAtivas.length * 4 * 4; // turmas * semanas * aulas por semana (aproximação)
      const aulasAnteriores = aulasRealizadas * 0.87; // Aproximação
      const variacaoAulas = aulasAnteriores > 0 
        ? ((aulasRealizadas - aulasAnteriores) / aulasAnteriores) * 100 
        : 0;

      // Dados para gráfico de receita mensal (baseado no período selecionado)
      const revenueData = [];
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const month = currentDate.getMonth();
        const year = currentDate.getFullYear();
        
        const mensalidadesMes = mensalidadesPeriodo.filter(m => {
          if (!m.dataVencimento) return false;
          const dataVenc = new Date(m.dataVencimento);
          return dataVenc.getMonth() === month && dataVenc.getFullYear() === year;
        });
        
        const receita = mensalidadesMes
          .filter(m => m.dataPagamento && m.dataPagamento !== '' && m.status === 'pago')
          .reduce((sum, m) => sum + (m.valorPago || m.valor || 0), 0);
        
        const alunosMes = alunosPeriodo.filter(a => {
          if (!a.createDate) return false;
          const dataCriacao = new Date(a.createDate);
          return dataCriacao.getMonth() <= month && dataCriacao.getFullYear() <= year;
        }).length;
        
        revenueData.push({
          month: currentDate.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
          revenue: receita,
          students: alunosMes
        });
        
        // Avançar para o próximo mês
        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      // Distribuição por níveis (baseado nos alunos do período)
      const levelDistribution = niveis.map(nivel => {
        const alunosNivel = alunosPeriodo.filter(a => a.nivelId === nivel.id && a.bAtivo !== false).length;
        return {
          name: nivel.nome,
          value: alunosNivel,
          color: nivel.cor || '#3b82f6'
        };
      }).filter(item => item.value > 0);

      return {
        metrics: {
          receitaTotal,
          totalAlunos,
          taxaOcupacao: Math.round(taxaOcupacao),
          aulasRealizadas,
          variacaoReceita: Math.round(variacaoReceita * 10) / 10,
          variacaoAlunos: Math.round(variacaoAlunos * 10) / 10,
          variacaoOcupacao: Math.round(variacaoOcupacao * 10) / 10,
          variacaoAulas: Math.round(variacaoAulas * 10) / 10
        },
        revenueData,
        levelDistribution
      };
    } catch (error) {
      console.error('❌ Erro ao buscar dados de relatórios:', error);
      throw error;
    }
  }
}

export const firebaseService = new FirebaseService();
